/* Product card behaviour — filter tabs, metal swatches, add to cart.

   Carried over from prototype/pages/home/home.js. The filter-tab logic, the
   is-active toggles and the aria-selected updates are unchanged.

   Everything else the prototype only mimed (audit 3.2):

   - Swatches were inert <i> decoration. Metal colour is a variant option and the
     pricing engine prices per variant, so selecting a swatch now swaps the price,
     the struck-through price, the discount badge, the image and the variant the
     cart button will add. Delegation means a filtered or re-rendered grid needs no
     re-binding.
   - The cart button set its own label to "Added" and stopped. It now posts to
     /cart/add.js, re-renders the cart drawer from the server and opens it.
   - The heart toggled aria-pressed and forgot. wishlist.js owns that now, so the
     state survives a reload and matches every other heart for the same product.
*/

(() => {
  if (window.CaratKinProductCards) return;
  window.CaratKinProductCards = true;

  // Customer-facing strings come from the locale file via the layout, so they are
  // translatable rather than buried in JavaScript as the prototype had them.
  const t = window.CaratKinStrings || {};

  /* ---- Filter tabs ------------------------------------------------------- */

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-product-filter]");
    if (!button) return;

    const group = button.closest("[role='tablist']");
    const section = button.closest("section");
    if (!group || !section) return;

    // The consultation rows filter with a visible-count limit and a load-more, so
    // consult-products.js owns their tabs. Two handlers on the same tab would fight.
    if (section.querySelector("[data-consult-grid]")) return;

    const selectedCategory = button.dataset.productFilter;

    group.querySelectorAll("[data-product-filter]").forEach((filterButton) => {
      const isSelected = filterButton === button;
      filterButton.classList.toggle("is-active", isSelected);
      filterButton.setAttribute("aria-selected", String(isSelected));
    });

    section.querySelectorAll("[data-home-product]").forEach((card) => {
      card.hidden = selectedCategory !== "all" && card.dataset.category !== selectedCategory;
    });
  });

  /* ---- Metal swatches --------------------------------------------------- */

  function selectSwatch(swatch) {
    const card = swatch.closest("[data-home-product]");
    if (!card) return;

    card.querySelectorAll(".swatch").forEach((candidate) => {
      const isSelected = candidate === swatch;
      candidate.classList.toggle("is-selected", isSelected);
      candidate.setAttribute("aria-pressed", String(isSelected));
    });

    card.dataset.variantId = swatch.dataset.variantId || "";

    const price = card.querySelector("[data-card-price]");
    const compare = card.querySelector("[data-card-compare]");
    const discount = card.querySelector("[data-card-discount]");
    const image = card.querySelector("[data-card-image]");
    const cartButton = card.querySelector("[data-home-cart]");

    if (price && swatch.dataset.variantPrice) price.textContent = swatch.dataset.variantPrice;

    // Hidden rather than emptied, so an undiscounted variant does not leave a gap
    // where the struck-through price was.
    if (compare) {
      compare.textContent = swatch.dataset.variantCompare || "";
      compare.hidden = !swatch.dataset.variantCompare;
    }
    if (discount) {
      discount.textContent = swatch.dataset.variantDiscount || "";
      discount.hidden = !swatch.dataset.variantDiscount;
    }
    // Swap the card image to the variant image. srcset (and any <source>) must be
    // cleared first, or the browser keeps painting a srcset candidate and ignores
    // the new src — so the picture would not change on colour switch.
    if (image && swatch.dataset.variantImage) {
      image.srcset = "";
      image.removeAttribute("srcset");
      image.src = swatch.dataset.variantImage;
    }
    if (cartButton) cartButton.disabled = swatch.dataset.variantAvailable === "false";
  }

  document.addEventListener("click", (event) => {
    const swatch = event.target.closest(".swatch[data-variant-id]");
    if (swatch) selectSwatch(swatch);
  });

  // The design's swatches are <i>, so they are given role="button" and tabindex in
  // the snippet; keyboard activation has to be wired by hand.
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const swatch = event.target.closest?.(".swatch[data-variant-id]");
    if (!swatch) return;
    event.preventDefault();
    selectSwatch(swatch);
  });

  /* ---- Add to cart ------------------------------------------------------ */

  const root = () => window.Shopify?.routes?.root || "/";

  async function addToCart(card, button) {
    const variantId = card.dataset.variantId;
    if (!variantId) return;

    const items = [{ id: Number(variantId), quantity: 1 }];
    // Guests sign in first; auth-gate.js adds this line once they are back.
    if (window.CaratKinAuthGate?.intercept({ kind: "cart", items }, () => button.click())) return;

    const label = button.querySelector("span");
    const original = label?.textContent;
    button.disabled = true;
    if (label) label.textContent = t.adding || "Adding…";

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const problem = await response.json().catch(() => null);
        throw new Error(problem?.description || `Add to cart failed: ${response.status}`);
      }

      card.classList.add("is-added");
      if (label) label.textContent = t.added || "Added";
      button.setAttribute("aria-label", t.addedToCart || "Added to cart");

      await window.CaratKinCartDrawer?.refresh();
      window.CaratKinCartDrawer?.open();
    } catch (error) {
      console.error(error);
      if (label) label.textContent = t.addFailed || "Could not add — try again";
      window.setTimeout(() => {
        if (label) label.textContent = original;
      }, 2400);
    } finally {
      button.disabled = false;
    }
  }

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-home-cart]");
    if (!button || button.disabled) return;
    const card = button.closest("[data-home-product]");
    if (card) addToCart(card, button);
  });
})();
